import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { setAccessToken, clearAccessToken } from "@/lib/api";

const AuthContext = createContext(null);

// Detect preview environment from the backend URL
const IS_PREVIEW = (process.env.REACT_APP_BACKEND_URL || "").includes("preview");

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      if (data && data.id) {
        setUser(data);
        setLoading(false);
        return;
      }
    } catch {
      // Access token expired or missing — the 401 interceptor in api.js
      // will automatically try a refresh via httpOnly cookie.
      // If that also fails we land here.
    }
    // No valid session — user must log in
    setUser(null);
    setLoading(false);
  }, []); // api is a stable module import

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const loginAdmin = async (username, password) => {
    const { data } = await api.post("/auth/admin/login", { username, password });
    // Backend sets httpOnly refresh cookie. Store access token in memory only.
    setAccessToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const registerAdmin = async (payload) => {
    const { data } = await api.post("/auth/admin/register", payload);
    setAccessToken(data.token);
    setUser(data.user);
    return data;
  };

  const previewLogin = async (password) => {
    const { data } = await api.post("/auth/preview-login", { password });
    setAccessToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const loginMember = async (member_id, password = "") => {
    const { data } = await api.post("/auth/member/login", { member_id, password });
    // If temp password, return data but DON'T set user yet — let the caller
    // show the change-password dialog first.
    if (data.must_change_password) {
      setAccessToken(data.token);
      // Don't call setUser yet — caller will handle after password change
      return data;
    }
    setAccessToken(data.token);
    setUser(data.user);
    return data;
  };

  const logout = async () => {
    clearAccessToken();
    setUser(null);
    try { await api.post("/auth/logout"); } catch { /* best-effort cookie clear */ }
  };

  const isAdmin = user && (user.role === "admin" || user.role === "primary_admin");
  const isMember = user && user.role === "member";
  const isPreview = IS_PREVIEW;

  return (
    <AuthContext.Provider value={{ user, loading, isAdmin, isMember, isPreview, loginAdmin, registerAdmin, previewLogin, loginMember, logout, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
